// Library methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Hooks
import { useEMRExportCounts } from "src/services/emr/hooks/emr-exports/useEMRExportCounts";
import { useEMRExports } from "src/services/emr/hooks/emr-exports/useEMRExports";
import { useRetryEMRExport } from "src/services/emr/hooks/emr-exports/useRetryEMRExport";

// Types
import { EMRExportState } from "src/services/emr/types";

// Styles
import {
  getRequiredErrorColor,
  getSurfaceTokens,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

const OUTBOX_STATES: Array<EMRExportState> = ["Failed", "Pending"];
const OUTBOX_LIMIT = 50;

const formatDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "N/A";

type ReportOutboxType = {
  clinicId: string | undefined;
  connectionId: string;
};

const ReportOutbox = ({ clinicId, connectionId }: ReportOutboxType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText, onSurface } = getSurfaceTokens(theme);
  const errorColor = getRequiredErrorColor(theme);

  const [open, setOpen] = useState(false);

  const { data: counts } = useEMRExportCounts(clinicId, connectionId, true);
  const { data: exports, isLoading } = useEMRExports(
    clinicId,
    connectionId,
    OUTBOX_STATES,
    OUTBOX_LIMIT,
    open
  );
  const {
    mutate: retry,
    isPending: isRetrying,
    variables,
  } = useRetryEMRExport(clinicId, connectionId);

  const summary = [
    {
      label: t("Pending"),
      value: (counts?.pending ?? 0) + (counts?.claimed ?? 0),
    },
    { label: t("Sent"), value: counts?.delivered ?? 0 },
    { label: t("Failed"), value: counts?.failed ?? 0, isError: true },
  ];

  return (
    <Box
      data-testid="report-outbox"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "8px",
        borderRadius: "4px",
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
        flexBasis: "100%",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="14px" fontWeight={600} color={mutedText}>
          {t("Report Outbox")}
        </Typography>
        <Button
          sx={{
            ...ModalCustomCancel(),
            minWidth: 0,
            height: "30px",
            p: "0px 8px",
          }}
          onClick={() => setOpen(true)}
        >
          {t("View")}
        </Button>
      </Box>
      <Box display="flex" gap="16px">
        {summary.map((item) => (
          <Box key={item.label} display="flex" flexDirection="column">
            <Typography fontSize="12px" color={mutedText}>
              {item.label}
            </Typography>
            <Typography
              fontSize="14px"
              fontWeight={600}
              color={item.isError && item.value > 0 ? errorColor : onSurface}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <DefaultModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={t("Report Outbox")}
        maxWidth="sm"
        modalActions={
          <Box display="flex" justifyContent="flex-end">
            <Button sx={ModalCustomCancel} onClick={() => setOpen(false)}>
              {t("Close")}
            </Button>
          </Box>
        }
      >
        <Box display="flex" flexDirection="column" gap="8px" p="16px">
          <Typography fontSize="12px" color={mutedText}>
            {t(
              "Failed reports retry by themselves for about 20 hours. After that they wait here until you retry them."
            )}
          </Typography>
          {isLoading && (
            <Typography fontSize="14px">{t("Loading...")}</Typography>
          )}
          {!isLoading && !exports?.length && (
            <Typography fontSize="14px">
              {t("No reports waiting or failed.")}
            </Typography>
          )}
          {exports?.map((item) => (
            <Box
              key={item.exportId}
              data-testid="report-outbox-item"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
                p: "8px",
                borderRadius: "4px",
                border: `1px solid ${
                  item.state === "Failed" ? errorColor : mutedText
                }`,
              }}
            >
              <Box display="flex" flexDirection="column" minWidth={0}>
                <Typography
                  fontSize="14px"
                  fontWeight={600}
                  color={item.state === "Failed" ? errorColor : onSurface}
                >
                  {t(item.state)}
                </Typography>
                <Typography fontSize="12px" color={mutedText}>
                  {t("Updated")}: {formatDateTime(item.updatedAt)} ·{" "}
                  {t("Attempts")}: {item.claimCount}
                </Typography>
                {item.lastError && (
                  <Typography
                    fontSize="12px"
                    color={onSurface}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {item.lastError}
                  </Typography>
                )}
              </Box>
              {item.state === "Failed" && (
                <Button
                  sx={ModalCustomConfirm}
                  disabled={isRetrying && variables === item.exportId}
                  onClick={() => retry(item.exportId)}
                >
                  {t("Retry")}
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </DefaultModal>
    </Box>
  );
};

export default ReportOutbox;
